import { IconChevronDoubleLeft } from 'central-icons/IconChevronDoubleLeft';
import { IconChevronDoubleRight } from 'central-icons/IconChevronDoubleRight';
import { IconChevronDownSmall } from 'central-icons/IconChevronDownSmall';
import classNames from 'classnames';
import { type RefObject, useId, useState } from 'react';

import { IconButton } from '@revelio/ui';

import styles from './ReportFlow.module.css';
import type { ReportFlowDocument } from './report-flow-types';

const scrollToAnchor = (container: HTMLElement | null, anchorId: string) => {
  const target = document.getElementById(anchorId);
  if (!container || !target) return;
  const scrollMargin = Number.parseFloat(
    getComputedStyle(target).scrollMarginTop
  );
  const top =
    target.getBoundingClientRect().top -
    container.getBoundingClientRect().top +
    container.scrollTop -
    (Number.isNaN(scrollMargin) ? 0 : scrollMargin);
  container.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
};

export const ReportFlowToc = ({
  doc,
  activeSectionId,
  scrollContainerRef,
  variant = 'full',
}: {
  doc: ReportFlowDocument;
  activeSectionId?: string;
  scrollContainerRef: RefObject<HTMLElement | null>;
  variant?: 'full' | 'compact';
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const listId = useId();

  if (doc.toc.length === 0) return null;

  if (isCollapsed) {
    return (
      <nav
        className={classNames(styles.toc, styles.tocCollapsed)}
        aria-label="Table of contents"
        data-testid="report-flow-toc"
      >
        <IconButton
          size="sm"
          variant="ghost"
          icon={<IconChevronDoubleRight size={16} />}
          aria-label="Show table of contents"
          aria-expanded={false}
          onClick={() => setIsCollapsed(false)}
          data-testid="report-flow-toc-expand"
        />
      </nav>
    );
  }

  const navigateTo = (anchorId: string) => {
    setIsOpen(false);
    scrollToAnchor(scrollContainerRef.current, anchorId);
  };

  return (
    <nav
      className={classNames(
        styles.toc,
        variant === 'compact' && styles.tocCompact,
        isOpen && styles.tocOpen
      )}
      aria-label="Table of contents"
      data-testid="report-flow-toc"
    >
      <div className={styles.tocHeader}>
        <h2 className={styles.tocTitle}>Table of contents</h2>
        <IconButton
          size="sm"
          variant="ghost"
          className={styles.tocCollapseButton}
          icon={<IconChevronDoubleLeft size={16} />}
          aria-label="Hide table of contents"
          aria-expanded
          onClick={() => setIsCollapsed(true)}
          data-testid="report-flow-toc-collapse"
        />
      </div>
      <button
        type="button"
        className={styles.tocToggle}
        aria-expanded={isOpen}
        aria-controls={listId}
        onClick={() => setIsOpen((open) => !open)}
        data-testid="report-flow-toc-toggle"
      >
        Table of contents
        <IconChevronDownSmall size={16} className={styles.tocToggleIcon} />
      </button>
      <ul id={listId} className={styles.tocList}>
        {doc.toc.map((entry) => {
          const isActive = entry.sectionId === activeSectionId;
          return (
            <li key={entry.anchorId}>
              <button
                type="button"
                className={classNames(
                  styles.tocEntry,
                  isActive && styles.tocEntryActive
                )}
                aria-current={isActive ? 'location' : undefined}
                onClick={() => navigateTo(entry.anchorId)}
              >
                {entry.label}
              </button>
              {entry.children.length > 0 && (
                <ul className={styles.tocChildList}>
                  {entry.children.map((child) => (
                    <li key={child.anchorId}>
                      <button
                        type="button"
                        className={classNames(
                          styles.tocEntry,
                          styles.tocChildEntry
                        )}
                        onClick={() => navigateTo(child.anchorId)}
                      >
                        {child.label}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
};
